import { IonPage, IonContent, useIonRouter } from "@ionic/react";
import { useEffect, useState, useCallback } from "react";

import "./WebSign.scss";
import Toolbar from "../components/Toolbar";
import { t } from "i18next";
import { RouteComponentProps } from "react-router";

import { Filesystem, Directory, Encoding } from "@capacitor/filesystem";

import { Capacitor } from "@capacitor/core";

import {
  SignplySDKParams,
  SignplySDKWidgetCustomText,
  // SignplySDKFileProperties,
  SignplySdkPlugin,
} from "capacitor-signply-sdk";

import { useAppDispatch } from "../app/hooks";

import { setSignatureComplete } from "../features/notification/notificationSlice";

interface ParamsDetails
  extends RouteComponentProps<{
    type: string;
    id: string;
  }> {}

function decodeBase64(base64String: string): string {
  try {
    // Decode the base64 string
    const decodedString = atob(base64String);

    // Convert the decoded string to a readable format
    let result = "";
    for (let i = 0; i < decodedString.length; i++) {
      const char = decodedString.charCodeAt(i);
      if (char < 128) {
        result += String.fromCharCode(char);
      } else {
        result += "\\x" + char.toString(16).padStart(2, "0");
      }
    }

    return result;
  } catch (error) {
    console.error("Error decoding base64:", error);
    return "Error decoding base64";
  }
}

const SignReport: React.FC<ParamsDetails> = ({ match }) => {
  const [localFilePath, setLocalFilePath] = useState<string | null>(null);
  const isNativePlatform = Capacitor.isNativePlatform();
  const dispatch = useAppDispatch();
  const navigation = useIonRouter();

  const handleSignatureComplete = useCallback(() => {
    setLocalFilePath(null);
    dispatch(setSignatureComplete(true));
    // Create confetti animation styles once
    const styleId = "confetti-animation-style";
    if (!document.getElementById(styleId)) {
      const style = document.createElement("style");
      style.id = styleId;
      style.textContent = `
        @keyframes fall {
          from { transform: translateY(0) rotate(0deg) scale(1); opacity: 1; }
          to { transform: translateY(110vh) rotate(360deg) scale(1); opacity: .2; }
        }
      `;
      document.head.appendChild(style);
    }

    // Create multiple confetti elements with varied emojis and timing
    const confettiEmojis = ["🎊", "🎉", "✨", "🎈", "🎆"];
    for (let i = 0; i < 8; i++) {
      const confetti = document.createElement("div");
      confetti.innerHTML = confettiEmojis[i % confettiEmojis.length];
      confetti.style.position = "fixed";
      confetti.style.fontSize = 1.5 + Math.random() + "em";
      confetti.style.left = Math.random() * 100 + "%";
      confetti.style.top = `-${2.5 + 5 * Math.random()}em`; // Start slightly above the viewport
      // confetti.style.zIndex = "9999";
      confetti.style.animation = `fall ${2 + Math.random() * 0.5}s`;
      document.body.appendChild(confetti);
      setTimeout(() => confetti.remove(), 4500);
    }
    navigation.push("/notificaciones");
  }, [dispatch, navigation]);

  // THIS IS FOR WEB VIEW
  const urlPath = `${localStorage.getItem("server_url")}/WebSignSpecific/?reportType=${match.params.type}&reportId=${match.params.id}`;
  // TODO URL ONLY WORKS FOR PRL-TESTING WHERE BOTH PORTALBACK AND EVOPREVEN ARE ON ONE URL WITH reverse proxys

  // download pdf
  const downloadPdf = useCallback(async () => {
    const urlPath = `${localStorage.getItem("server_url")}/api/request_report/?report=${match.params.type}&report-id=${match.params.id}`;

    try {
      const response = await fetch(urlPath, {
        method: "POST",
        body: '{"password":"bm4bL3Zz"}',
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access")}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const currentDate = new Date()
        .toLocaleDateString("en-GB")
        .replace(/\//g, "_");
      const randomChars = Math.random().toString(36).substring(2, 7);
      const fileName = `report_${currentDate}_${randomChars}.pdf`;

      const blob = await response.blob();
      const file = new File([blob], fileName, {
        type: "application/pdf",
      });

      const reader = new FileReader();

      reader.onloadend = async () => {
        const base64Data = reader.result?.toString().split(",")[1];
        if (base64Data) {
          try {
            const result = await Filesystem.writeFile({
              path: fileName,
              data: base64Data,
              directory: Directory.External,
              recursive: true,
              // encoding: Encoding.UTF8,
            });
            setLocalFilePath(result.uri);
            // console.warn("File saved at:", result.uri);
          } catch (writeError) {
            console.error("Error writing file:", writeError);
          }
        }
      };

      reader.onerror = (error) => {
        console.error("FileReader error:", error);
      };

      reader.readAsDataURL(blob);
    } catch (error) {
      console.error("Error downloading PDF:", error);
    }
  }, [match.params.type, match.params.id]);

  async function checkPdfHealth(filePath: string): Promise<boolean> {
    try {
      // 1. Check file size
      const fileInfo = await Filesystem.stat({
        path: filePath,
        directory: Directory.External,
      });

      if (fileInfo.size < 100) {
        // Adjust this threshold as needed
        console.warn("PDF file is suspiciously small");
        return false;
      }

      // 2 & 3. Verify PDF header and try to read file
      const fileContents = await Filesystem.readFile({
        path: filePath,
        directory: Directory.External,
      });

      let fileData = "";
      if (typeof fileContents.data === "string") {
        fileData = decodeBase64(fileContents.data);
        console.warn("Decoded content:", fileData.substring(0, 50));
      } else {
        console.warn("File contents is not a string, unable to decode");
      }

      // Check for PDF signature
      if (!fileData.startsWith("%PDF-")) {
        console.warn("File does not start with PDF signature");
        return false;
      }

      // Additional checks could be performed here
      // Check for EOF marker
      if (!fileData.includes("%%EOF")) {
        console.warn("File does not contain EOF marker");
        return false;
      }

      return true;
    } catch (error) {
      console.error("Error checking PDF health:", error);
      return false;
    }
  }

  const uploadFile = useCallback(
    async (filePath: string) => {
      try {
        // fetch the local signed file
        const file = await Filesystem.readFile({
          path: `signed_${filePath.split("/").pop()}`,
          directory: Directory.External,
        });

        // Convert base64 to Blob
        // Check if file.data is a string before using atob
        if (typeof file.data !== "string") {
          throw new Error("File data is not a string");
        }

        // Use decodeBase64 function and create Blob directly
        const decodedData = decodeBase64(file.data);
        const blob = new Blob([decodedData], { type: "application/pdf" });

        // Create FormData and append the blob
        const formData = new FormData();
        formData.append("signed-file", blob);

        try {
          const response = await fetch(
            `${localStorage.getItem("server_url")}/api/upload_signed_report/?report=${match.params.type}&report-id=${match.params.id}`,
            {
              method: "POST",
              headers: {
                Authorization: `Bearer ${localStorage.getItem("access")}`,
              },
              body: formData,
            },
          );

          console.warn("Upload file:", response.status);
          if (response.ok) {
            const data = await response.json();
            console.warn("Upload successful! Server response:", data);
            console.error("Upload successful! Server response:", data);
            alert("File uploaded successfully!");
          } else {
            console.error(
              "Upload failed! Server response:",
              response.statusText,
            );
            alert(`Upload failed! ${response.statusText}`);
          }
        } catch (error) {
          console.error("Upload File Error:", error);
        }
      } catch (error) {
        console.error("Error uploading document:", error);
        alert(`Error uploading document: ${error}`);
      }
    },
    [match.params.type, match.params.id],
  );

  const signDocument = useCallback(async (filePath: string): Promise<void> => {
    try {
      const params = new SignplySDKParams();
      const signplySdkWidgetCustomText = new SignplySDKWidgetCustomText(
        6,
        "firmado digitalmente",
      );

      params.licenseB64 = `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`;

      params.widget.widgetCustomText = [signplySdkWidgetCustomText];

      params.fileProperties = {
        // @ts-ignore
        documentPath: filePath.split("/").pop(),
        signedName: `signed_${filePath.split("/").pop()}`,
        isShareable: true,
        password: "",
        author: "Tecnopreven",
        reason: "Firma de documento",
        contact: "admin@tecnopreven.com",
        location: "Barcelona",
      };

      // params.extra.fullScreen = true;
      params.commonProperties.title = "Firma de documento";

      params.widget.widgetType = "Float";
      const signHere = document.evaluate(
        "//*[contains(text(), '!!!!!!!!!!')]",
        document,
        null,
        XPathResult.ANY_TYPE,
        null,
      );

      if (signHere) {
        // forced sign location
        params.widget.widgetFloatText = "!!!!!!!!!!";
      } else {
        params.widget.widgetFloatText = "Recibí:"; // TODO & FIXME: Catalan / language proof like we made in Multisign with invisible anchor text id tag
        // if ("{{who_is}}") {
        //     webSignConfigJson.widget.type = "float";
        //     if ("{{who_is}}" === "responsible") {
        //         webSignConfigJson.widget.floatText = "Entregado por:";
        //     } else if ("{{who_is}}" === "worker") {
        //         webSignConfigJson.widget.floatText = "Recibí:";
        //     }
        // }
      }

      params.widget.widgetWidth = 100;
      params.widget.widgetHeight = 60;
      params.widget.widgetFloatGapX = 0;
      params.widget.widgetFloatGapY = -10;

      const { result } = await SignplySdkPlugin.signDocument({
        signplySDKParams: JSON.stringify(params),
      });

      // alert(result); // "File saved at xyz"

      // uploadFile();
    } catch (error) {
      console.error("Error signing document:", error);
      alert(`Error signing document: ${error}`);
    }
  }, []);

  useEffect(() => {
    if (isNativePlatform) {
      console.warn("Downloading PDF...");
      downloadPdf();
    }
  }, [downloadPdf, isNativePlatform]);

  useEffect(() => {
    const signAndUpload = async () => {
      if (!isNativePlatform || !localFilePath) return;

      let isPdfHealthy = await checkPdfHealth(
        `${localFilePath.split("/").pop()}`,
      );

      if (isPdfHealthy) {
        console.warn("Downloaded PDF is healthy");
      } else {
        console.error("Downloaded PDF health check failed");
        alert("The downloaded PDF appears to be corrupt or invalid somehow.");
      }

      console.warn("Signing PDF...");
      await signDocument(localFilePath);

      isPdfHealthy = await checkPdfHealth(
        `signed_${localFilePath.split("/").pop()}`,
      );

      if (isPdfHealthy) {
        console.warn("Signed PDF is healthy");
      } else {
        console.error("Signed PDF health check failed");
        alert("The signed PDF appears to be corrupt or invalid.");
      }

      await uploadFile(localFilePath);
      handleSignatureComplete();
    };

    signAndUpload();
  }, [
    localFilePath,
    isNativePlatform,
    signDocument,
    uploadFile,
    handleSignatureComplete,
  ]);

  // Listener para detectar cuando la firma web se complete
  useEffect(() => {
    if (isNativePlatform) return;

    const handleMessage = (event: MessageEvent) => {
      console.log("Mensaje recibido del iframe:", event.data, "Origin:", event.origin);
      
      // WebSign específicamente envía un evento con type: "signed" cuando la firma se completa
      if (event.data && typeof event.data === 'object') {
        if (event.data.type === 'signed') {
          console.log("✅ Firma completada detectada - WebSign event.type === 'signed'");
          console.log("Datos de la firma:", event.data);
          handleSignatureComplete();
          return;
        }
        
        // Fallbacks para otros posibles formatos
        if (event.data.type === 'signature-complete' || 
            event.data.action === 'signed' || 
            event.data.status === 'completed') {
          console.log("✅ Firma completada detectada (formato alternativo)");
          handleSignatureComplete();
          return;
        }
      }
      
      // También verificar mensajes de texto simples como fallback
      if (typeof event.data === 'string') {
        if (event.data.includes('signed') || 
            event.data.includes('completed') || 
            event.data.includes('success')) {
          console.log("✅ Firma completada detectada (string)");
          handleSignatureComplete();
          return;
        }
      }
    };

    window.addEventListener('message', handleMessage);
    
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [handleSignatureComplete, isNativePlatform]);

  // this debug is buggy careful
  // useEffect(() => {
  //   // debug it
  //   setTimeout(() => {
  //     uploadFile("test");
  //     handleSignatureComplete();
  //   }, 3000);
  // }, [handleSignatureComplete, uploadFile]);

  return (
    <IonPage>
      <Toolbar title={t("Firmando")} />
      <IonContent fullscreen>
        {isNativePlatform ? (
          <h2>Opening Signply...</h2>
        ) : (
          <div style={{ height: "100%" }}>
            {/* Botón temporal para pruebas - remover en producción */}
            <div style={{ 
              position: 'absolute', 
              top: '10px', 
              right: '10px', 
              zIndex: 1000,
              backgroundColor: 'rgba(0,0,0,0.7)',
              padding: '10px',
              borderRadius: '5px'
            }}>
              <button 
                onClick={() => {
                  console.log("Botón de prueba clickeado - simulando firma completada");
                  handleSignatureComplete();
                }}
                style={{
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  padding: '8px 12px',
                  borderRadius: '3px',
                  cursor: 'pointer',
                  fontSize: '12px'
                }}
              >
                ✓ Simular Firma Completada
              </button>
            </div>
            <iframe
              src={urlPath}
              name="websign-frame"
              title="Web Sign Frame"
              height="100%"
              width="100%"
            ></iframe>
          </div>
        )}
      </IonContent>
    </IonPage>
  );
};

export default SignReport;
